import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';
import { ThemeToggle } from './ThemeToggle.js';

interface NavSection { label?: string; items: { to: string; label: string }[] }

type Access = 'loading' | 'approved' | 'pending' | 'rejected';

export function Layout() {
  const { user, getToken, signOutNow } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [onboarded, setOnboarded] = useState<boolean | null>(null);
  const [access, setAccess] = useState<Access>('loading');
  const [me, setMe] = useState<any>(null);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer whenever the user navigates.
  useEffect(() => { setNavOpen(false); }, [location.pathname, location.search]);

  // Lock body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (navOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  useEffect(() => {
    (async () => {
      try {
        const api = new ApiClient(getToken);
        const m: any = await api.me();
        setMe(m);
        setIsAdmin(!!m.isAdmin);
        setOnboarded(!!m?.user?.onboarding?.voiceProfileReady);
        setAccess(((m.accessStatus ?? 'pending') as Access));
      } catch {
        setOnboarded(false);
        setAccess('pending');
      }
    })();

    // Re-push a fresh Firebase ID token to the extension on every load. Firebase
    // tokens expire after 1 hour, so without this the extension's stored token goes
    // stale and the passive capture API call silently 401s.
    (async () => {
      try {
        const { pingExtension, refreshExtensionToken } = await import('../lib/extensionBridge.js');
        const ping = await pingExtension();
        if (!ping.installed) return;
        const token = await getToken();
        if (!token) return;
        const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';
        await refreshExtensionToken(token, apiBase);
      } catch { /* extension may not be installed — that's fine */ }
    })();
    /* eslint-disable-next-line */
  }, []);

  // Gate: non-admin users without approval see the waitlist screen, not the app.
  if (access === 'loading') {
    return <div className="loading">Loading…</div>;
  }
  if (!isAdmin && (access === 'pending' || access === 'rejected')) {
    return <WaitlistScreen email={user?.email ?? ''} status={access} onSignOut={signOutNow} />;
  }

  const sections: NavSection[] = [
    {
      items: [
        ...(onboarded ? [{ to: '/app/home', label: 'Home' }] : [{ to: '/app/onboarding', label: 'Get started' }]),
      ],
    },
    {
      label: 'Account',
      items: [
        { to: '/app/voice', label: 'Voice' },
        { to: '/app/history', label: 'History' },
        { to: '/app/extension', label: 'Chrome extension' },
        { to: '/app/billing', label: 'Billing' },
        { to: '/app/settings', label: 'Settings' },
        { to: '/app/help', label: 'Help' },
      ],
    },
  ];

  return (
    <div className={`layout${navOpen ? ' nav-open' : ''}`}>
      {/* Mobile-only top bar with hamburger + brand. Hidden on desktop via CSS. */}
      <header className="mobile-bar">
        <button className="hamburger" onClick={() => setNavOpen(v => !v)} aria-label={navOpen ? 'Close menu' : 'Open menu'}>
          <span /><span /><span />
        </button>
        <NavLink to={onboarded ? '/app/home' : '/app/onboarding'} className="mobile-bar-brand">tweetvia</NavLink>
      </header>
      {/* Backdrop closes the drawer on tap. */}
      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
      <aside className="sidebar">
        <NavLink to={onboarded ? '/app/home' : '/app/onboarding'} className="logo-link">
          <h2 className="logo">tweetvia</h2>
        </NavLink>
        <nav>
          {sections.map((sec, i) => (
            <div key={i} className="nav-section">
              {sec.label && <div className="nav-section-label">{sec.label}</div>}
              {sec.items.map(n => (
                <NavLink key={n.to} to={n.to} end={n.to === '/app/home'} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
          {isAdmin && (
            <div className="nav-section">
              <div className="nav-section-label">Admin</div>
              <NavLink to="/app/admin" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Dashboard
              </NavLink>
              <NavLink to="/app/admin/ranking" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Ranking playbook
              </NavLink>
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="muted small sidebar-email">{user?.email}</div>
            <ThemeToggle />
          </div>
          <a className="sidebar-feedback" href="mailto:admin@taptapgo.org?subject=tweetvia%20feedback" title="Send feedback">Send feedback</a>
          <button className="ghost" onClick={() => signOutNow()}>Sign out</button>
        </div>
      </aside>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

function WaitlistScreen({ email, status, onSignOut }: { email: string; status: 'pending' | 'rejected'; onSignOut: () => void | Promise<void> }) {
  return (
    <div className="landing-wrap">
      <div className="landing-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <div className="signin-card" style={{ textAlign: 'center', maxWidth: 420 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>{status === 'rejected' ? "We're not able to grant access right now." : "You're on the waitlist."}</h2>
          {status === 'pending' ? (
            <p className="muted" style={{ margin: '0 0 16px' }}>
              tweetvia is invite-only while we polish things up. We'll email <strong>{email}</strong> when your access is approved — usually within a day.
            </p>
          ) : (
            <p className="muted" style={{ margin: '0 0 16px' }}>
              If you believe this is a mistake, reach out to <strong>admin@taptapgo.org</strong>.
            </p>
          )}
          <button onClick={() => onSignOut()}>Sign out</button>
        </div>
      </div>
    </div>
  );
}
